import { Autocomplete, Checkbox, Chip, CircularProgress, TextField } from '@mui/material';

interface InputMultiSelectProps<T extends { id: number }> {
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  getLabel: (option: T) => string;
  label: string;
  placeholder?: string;
  loading?: boolean;
  noOptionsText?: string;
  onScrollEnd?: () => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
}

function InputMultiSelect<T extends { id: number }>({
  options,
  value,
  onChange,
  getLabel,
  label,
  placeholder,
  loading,
  noOptionsText,
  onScrollEnd,
  inputValue,
  onInputChange,
}: InputMultiSelectProps<T>) {
  const isServerFiltered = Boolean(onInputChange);

  const optionsWithSelection = [...options, ...value.filter((item) => !options.some((o) => o.id === item.id))];

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    if (!onScrollEnd) return;

    const listbox = event.currentTarget;
    const position = listbox.scrollTop + listbox.clientHeight;

    if (listbox.scrollHeight - position <= 1) {
      onScrollEnd();
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      fullWidth
      options={optionsWithSelection}
      value={value}
      onChange={(_event, nextValue) => onChange([...nextValue])}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={loading}
      noOptionsText={noOptionsText}
      {...(isServerFiltered
        ? {
            filterOptions: (unfiltered: T[]) => unfiltered,
            inputValue,
            onInputChange: (_event, next: string, reason: string) => {
              if (reason !== 'reset') {
                onInputChange?.(next);
              }
            },
          }
        : {})}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.id}>
          <Checkbox size='small' checked={selected} disableRipple sx={{ padding: '4px 9px 4px 0px' }} />
          {getLabel(option)}
        </li>
      )}
      renderValue={(tagValue, getItemProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return <Chip {...tagProps} key={key} size='small' label={getLabel(option)} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} sx={{ marginRight: 1 }} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      slotProps={{
        popper: {
          sx: { '& .MuiAutocomplete-option': { fontSize: '14px' } },
        },
        listbox: { onScroll: handleScroll },
      }}
      sx={{ '& .MuiInputLabel-root': { fontSize: '14px' } }}
    />
  );
}

export default InputMultiSelect;
